import { LocalEnvironmentName } from "waapi/environments-repository/constants/environments.js";
import { Languages } from "waapi/front-utils/hooks/useTranslation/types.js";
import { SecondaryAccountType } from "waapi/girbal/Store/types.js";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { isProduction } from "../../utils/constants.js";
import { EnvironmentState, TrialState, UseUserStateState } from "./userState.types.js";

export const useUserState = create<UseUserStateState>()(
    devtools(
        () => ({
            auth: {
                linkedinAccount: {},
                rootAccount: {
                    apiToken: "",
                    userId: "",
                    showLoginModal: false,
                },
                secondaryAccount: {
                    accountType: SecondaryAccountType.IMPERSONATION,
                    apiToken: "",
                    userId: "",
                },
            },
            environment: {
                apiUrl: "",
                socketUrl: "",
                maintenance: false,
                environments: {},
                selectedEnvironment: LocalEnvironmentName.LOCAL,
            },
            other: {
                extensionUuid: "",
            },
            language: {
                selected: Languages.EN,
            },
            hasLaunchFirstCampaign: false,
            isStaff: false,
            hasCloudModalDesactivated: false,
            hasToRefetchUserData: true,
        }),
        { name: "useUserState", store: "useUserState", enabled: !isProduction },
    ),
);

export const updateUserState = (params: Partial<UseUserStateState>) => {
    useUserState.setState(
        (state) => ({
            auth: {
                ...state.auth,
                rootAccount: params.auth?.rootAccount ?? state.auth.rootAccount,
                linkedinAccount: params.auth?.linkedinAccount ?? state.auth.linkedinAccount,
            },
            language: params.language ?? state.language,
            permissions: params.permissions ?? state.permissions,
            currentPlan: params.currentPlan ?? state.currentPlan,
            company: params.company ?? state.company,
            intercomIdentifiers: params.intercomIdentifiers ?? state.intercomIdentifiers,
            isStaff: params.isStaff ?? state.isStaff,
            hasLaunchFirstCampaign: params.hasLaunchFirstCampaign ?? state.hasLaunchFirstCampaign,
            other: params.other ?? state.other,
            hasToRefetchUserData: true,
        }),
        undefined,
        "updateUserState",
    );
};

export const setEnvironment = ({ environment }: { environment: EnvironmentState }) => {
    useUserState.setState({ environment }, undefined, "setEnvironment");
};

export const setUserPermissions = (permissions: UseUserStateState["permissions"]) => {
    useUserState.setState({ permissions }, undefined, "setUserPermissions");
};

export const setUserPlan = (currentPlan: UseUserStateState["currentPlan"]) => {
    useUserState.setState({ currentPlan }, undefined, "setUserPlan");
};

export const setTrial = ({ trial }: TrialState) => {
    useUserState.setState((state) => (state.currentPlan ? { currentPlan: { ...state.currentPlan, trial } } : state), undefined, "setTrial");
};

export const setUserCompany = (company: UseUserStateState["company"]) => {
    useUserState.setState({ company }, undefined, "setUserCompany");
};

export const setLanguage = (language: UseUserStateState["language"]) => {
    useUserState.setState({ language }, undefined, "setLanguage");
};

export const logToSecondaryAccount = (secondaryAccount: UseUserStateState["auth"]["secondaryAccount"]) => {
    useUserState.setState((state) => ({ company: undefined, auth: { ...state.auth, secondaryAccount } }), undefined, "logToSecondaryAccount");
};

export const logOutFromSecondaryAccount = () => {
    useUserState.setState(
        (state) => ({
            company: undefined,
            auth: {
                ...state.auth,
                secondaryAccount: {
                    accountType: SecondaryAccountType.IMPERSONATION,
                    apiToken: "",
                    userId: "",
                },
            },
        }),
        undefined,
        "logOutFromSecondaryAccount",
    );
};

export const setIsStaff = ({ isStaff }: { isStaff: boolean }) => {
    useUserState.setState({ isStaff }, undefined, "setIsStaff");
};

export const setHasCloudModalDesactivated = ({ hasCloudModalDesactivated }: { hasCloudModalDesactivated: boolean }) => {
    useUserState.setState({ hasCloudModalDesactivated }, undefined, "setHasCloudModalDesactivated");
};
